import React, { useEffect, useState } from 'react';
import { Alert, Button, Snackbar, Slide } from '@mui/material';
import type { SlideProps } from '@mui/material/Slide';
import { useLocation, useNavigate } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { WarehouseAPI } from '../../services/api';
import { tokens } from '../../theme/tokens';

// App-open nudge: a passive, non-blocking toast that fires AT MOST ONCE per browser login
// session when the signed-in user has open actionables. It reuses the SAME count endpoint the
// header Actionables badge uses (WarehouseAPI.getMyActionableCount) so the number always matches
// the badge — one source of truth, no new backend endpoint, no duplicate polling (this fires a
// single one-shot fetch on mount, the header keeps its own live polling).
//
// Once-per-session: tracked via sessionStorage under ACTIONABLES_ALERT_KEY. sessionStorage is
// scoped to the browser tab session, so a full page reload within the same session does NOT
// re-nag, but a fresh login in a new tab/session does. The "shown" flag is written as soon as we
// decide to show the toast (count > 0), guarded by a module-level ran-this-mount ref via the
// empty-dep effect, so route changes / re-renders / the header's react-query-less refetches can
// never re-fire it.
const ACTIONABLES_ALERT_KEY = 'salty_actionablesAlertShown';
const ACTIONABLES_ROUTE = '/actionables';

const SlideUp = (props: SlideProps) => <Slide {...props} direction="up" />;

const ActionablesOpenAlert: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  const role = user?.role || (user?.isAdmin ? 'admin' : 'viewer');
  // Photography-only users don't get an actionables badge in the header, so there's nothing to
  // nudge them about — mirror that role guard here.
  const isPhotographyOnly = role === 'photography';

  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  // Scope mirrors the count endpoint: "mine" = the count is the user's own assigned open items,
  // "all" = the count is the TEAM-WIDE open total (super-admins). The toast wording branches on
  // this so a super-admin isn't told team items are personally theirs.
  const [scope, setScope] = useState<'all' | 'mine'>('mine');

  useEffect(() => {
    // Guard order matters: only run the once-per-session check + fetch when we actually have an
    // eligible, signed-in user. The empty-ish deps below ([user, isPhotographyOnly]) mean this
    // effect does NOT re-run on route changes or re-renders — so it cannot re-nag per navigation.
    if (!user || isPhotographyOnly) return;

    // Already shown this session? Do nothing (survives page reload within the same tab session).
    let alreadyShown = false;
    try { alreadyShown = window.sessionStorage.getItem(ACTIONABLES_ALERT_KEY) === '1'; } catch { /* storage blocked */ }
    if (alreadyShown) return;

    // Suppress if the user already landed ON the Actionables page — no point nudging them to
    // where they already are. Checked at mount/eligibility time, not on every route change.
    if (location.pathname === ACTIONABLES_ROUTE) return;

    let cancelled = false;
    WarehouseAPI.getMyActionableCount()
      .then(c => {
        if (cancelled) return;
        // Silent on 0 (or a failed/empty fetch): show nothing.
        if (!c || c.open <= 0) return;
        // Mark shown as soon as we decide to surface it, so nothing can re-fire it this session.
        try { window.sessionStorage.setItem(ACTIONABLES_ALERT_KEY, '1'); } catch { /* storage blocked */ }
        setCount(c.open);
        setScope(c.scope);
        setOpen(true);
      })
      .catch(() => undefined); // Silent on failure.

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isPhotographyOnly]);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    // Let the auto-hide timer and the explicit dismiss (X) close it; ignore click-away so a
    // stray click elsewhere doesn't swallow the nudge prematurely.
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const handleView = () => {
    setOpen(false);
    // Match the toast's scope: a super-admin ("all" = team-wide count) is sent to the Team (all)
    // tab so they land where those items actually live; everyone else goes to My actionables.
    // `?tab=team` is honored only for super-admins (ActionablesPage gates it) and otherwise
    // safely falls back to 'mine', so this target is safe regardless of role.
    navigate(scope === 'all' ? `${ACTIONABLES_ROUTE}?tab=team` : ACTIONABLES_ROUTE);
  };

  if (!user || isPhotographyOnly) return null;

  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      // Match the app's existing snackbar placement (CardingTab): bottom-right. Respect
      // reduced-motion by skipping the slide transition when the user prefers reduced motion.
      TransitionComponent={reduceMotion ? undefined : SlideUp}
    >
      <Alert
        severity="info"
        variant="filled"
        onClose={handleClose}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={handleView}
            sx={{ fontWeight: 700 }}
          >
            View
          </Button>
        }
        sx={{
          alignItems: 'center',
          bgcolor: tokens.ink,
          color: tokens.surface,
          borderRadius: '10px',
          boxShadow: tokens.softShadow,
          '& .MuiAlert-icon': { color: tokens.orangeLight },
          '& .MuiAlert-action': { pt: 0, alignItems: 'center' },
        }}
      >
        {scope === 'all'
          ? `${count} open actionable${count === 1 ? '' : 's'} across the team`
          : `You have ${count} open actionable${count === 1 ? '' : 's'}`}
      </Alert>
    </Snackbar>
  );
};

export default ActionablesOpenAlert;
